import {
    Alert,
    Button,
    Descriptions,
    Input,
    Modal,
    notification,
    Space,
    Spin,
    Tag,
} from 'antd';
import { AxiosError } from 'axios';
import { localize } from 'mixins/helpers';
import React, { FC, useEffect, useState } from 'react';
import {
    getMyApiToken,
    MyApiToken,
} from '../actions';

interface ApiErrorResponse {
    message?: string;
}

interface ApiTokenModalProps {
    open: boolean;
    close: () => void;
    accessToken?: string;
    translations?: Record<string, string>;
}

const ApiTokenModal: FC<ApiTokenModalProps> = ({
    open,
    close,
    accessToken,
    translations,
}) => {
    const [tokenData, setTokenData] = useState<MyApiToken | null>(null);
    const [loading, setLoading] = useState(false);
    const [tokenNotIssued, setTokenNotIssued] = useState(false);

    const loadToken = async () => {
        setLoading(true);
        setTokenData(null);
        setTokenNotIssued(false);

        try {
            const response = await getMyApiToken(accessToken);
            setTokenData(response.data);
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            const message = axiosError.response?.data?.message;

            if (
                axiosError.response?.status === 404 &&
                message === 'error.api-token-request.token-not-issued'
            ) {
                setTokenNotIssued(true);
                return;
            }

            notification.error({
                message:
                    localize('error', translations) ||
                    'Ошибка',
                description:
                    localize('api-token-load-error', translations) ||
                    'Не удалось получить API-токен',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadToken();
        } else {
            setTokenData(null);
            setTokenNotIssued(false);
        }
    }, [open]);

    const copyToken = async () => {
        if (!tokenData?.token) {
            return;
        }

        try {
            await navigator.clipboard.writeText(tokenData.token);

            notification.success({
                message:
                    localize('api-token-copied', translations) ||
                    'API-токен скопирован',
            });
        } catch {
            notification.error({
                message:
                    localize('error', translations) ||
                    'Ошибка',
                description:
                    localize('api-token-copy-error', translations) ||
                    'Не удалось скопировать API-токен',
            });
        }
    };

    const downloadToken = () => {
        if (!tokenData) {
            return;
        }

        const content = [
            `Номер обращения: ${tokenData.requestNumber}`,
            `API-токен: ${tokenData.token}`,
            `Дата выдачи: ${tokenData.issuedAt}`,
            `Действует до: ${tokenData.expiredAt}`,
            `Статус: ${
                tokenData.blocked
                    ? 'Заблокирован'
                    : 'Активен'
            }`,
        ].join('\r\n');

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `api-token-${tokenData.requestNumber}.txt`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(url);
    };

    const handleClose = () => {
        setTokenData(null);
        setTokenNotIssued(false);
        close();
    };

    return (
        <Modal
            title={
                localize('api-token-modal-title', translations) ||
                'Ваш API-токен'
            }
            open={open}
            onCancel={handleClose}
            width={650}
            destroyOnClose
            footer={
                tokenData
                    ? [
                          <Button
                              key="copy"
                              onClick={copyToken}
                          >
                              {localize('copy', translations) ||
                                  'Скопировать'}
                          </Button>,
                          <Button
                              key="download"
                              type="primary"
                              onClick={downloadToken}
                          >
                              {localize('download', translations) ||
                                  'Скачать'}
                          </Button>,
                          <Button
                              key="close"
                              onClick={handleClose}
                          >
                              {localize('close', translations) ||
                                  'Закрыть'}
                          </Button>,
                      ]
                    : [
                          <Button
                              key="close"
                              onClick={handleClose}
                          >
                              {localize('close', translations) ||
                                  'Закрыть'}
                          </Button>,
                      ]
            }
        >
            <Spin spinning={loading}>
                {tokenNotIssued && (
                    <Alert
                        type="info"
                        showIcon
                        message={
                            localize(
                                'api-token-not-issued-title',
                                translations
                            ) || 'API-токен ещё не выдан'
                        }
                        description={
                            localize(
                                'api-token-not-issued-description',
                                translations
                            ) ||
                            'После согласования заявки API-токен появится в этом разделе.'
                        }
                    />
                )}

                {tokenData && (
                    <Space
                        direction="vertical"
                        size={16}
                        style={{ width: '100%' }}
                    >
                        <Descriptions
                            bordered
                            size="small"
                            column={1}
                        >
                            <Descriptions.Item
                                label={
                                    localize(
                                        'api-token-request-number',
                                        translations
                                    ) || 'Номер обращения'
                                }
                            >
                                {tokenData.requestNumber}
                            </Descriptions.Item>

                            <Descriptions.Item
                                label={
                                    localize(
                                        'api-token-issued-at',
                                        translations
                                    ) || 'Дата выдачи'
                                }
                            >
                                {tokenData.issuedAt}
                            </Descriptions.Item>

                            <Descriptions.Item
                                label={
                                    localize(
                                        'api-token-expired-at',
                                        translations
                                    ) || 'Действует до'
                                }
                            >
                                {tokenData.expiredAt}
                            </Descriptions.Item>

                            <Descriptions.Item
                                label={
                                    localize(
                                        'api-token-status',
                                        translations
                                    ) || 'Статус'
                                }
                            >
                                {tokenData.blocked ? (
                                    <Tag color="red">
                                        {localize(
                                            'api-token-blocked',
                                            translations
                                        ) || 'Заблокирован'}
                                    </Tag>
                                ) : (
                                    <Tag color="green">
                                        {localize(
                                            'api-token-active',
                                            translations
                                        ) || 'Активен'}
                                    </Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>

                        <div>
                            <div
                                style={{
                                    marginBottom: 8,
                                    fontWeight: 600,
                                }}
                            >
                                API-токен
                            </div>

                            <Input.TextArea
                                value={tokenData.token}
                                readOnly
                                autoSize={{
                                    minRows: 2,
                                    maxRows: 5,
                                }}
                                style={{
                                    fontFamily: 'monospace',
                                    resize: 'none',
                                }}
                            />
                        </div>

                        {tokenData.blocked && (
                            <Alert
                                type="warning"
                                showIcon
                                message={
                                    localize(
                                        'api-token-blocked-message',
                                        translations
                                    ) ||
                                    'API-токен заблокирован и недоступен для использования.'
                                }
                            />
                        )}
                    </Space>
                )}
            </Spin>
        </Modal>
    );
};

export default ApiTokenModal;